/**
 * Arena footprints are signed distance fields composed from primitives.
 *
 * Using an SDF rather than a polygon soup buys three things at once:
 *   • holes and cut-outs are just a subtraction (see the "Затон" ring arena);
 *   • the distance value drives the edge warning glow and the fall check;
 *   • the collapse system can ask "is this tile still inside at scale s?"
 *     without any per-arena special casing.
 *
 * Negative distance means inside.
 */
export interface Shape {
  distance(x: number, z: number): number
}

export class Circle implements Shape {
  constructor(
    readonly cx: number,
    readonly cz: number,
    readonly r: number,
  ) {}

  distance(x: number, z: number): number {
    return Math.hypot(x - this.cx, z - this.cz) - this.r
  }
}

export class Rect implements Shape {
  private readonly cos: number
  private readonly sin: number

  constructor(
    readonly cx: number,
    readonly cz: number,
    readonly halfX: number,
    readonly halfZ: number,
    readonly angle = 0,
  ) {
    this.cos = Math.cos(angle)
    this.sin = Math.sin(angle)
  }

  distance(x: number, z: number): number {
    const dx = x - this.cx
    const dz = z - this.cz
    const lx = dx * this.cos + dz * this.sin
    const lz = -dx * this.sin + dz * this.cos
    const qx = Math.abs(lx) - this.halfX
    const qz = Math.abs(lz) - this.halfZ
    const outside = Math.hypot(Math.max(qx, 0), Math.max(qz, 0))
    const inside = Math.min(Math.max(qx, qz), 0)
    return outside + inside
  }
}

export class Polygon implements Shape {
  /** Flat [x0, z0, x1, z1, …] for cache-friendly iteration. */
  private readonly pts: number[]

  constructor(points: Array<[number, number]>) {
    this.pts = points.flat()
  }

  distance(x: number, z: number): number {
    const p = this.pts
    const n = p.length / 2
    let d = Infinity
    let sign = 1

    for (let i = 0, j = n - 1; i < n; j = i++) {
      const ax = p[i * 2]
      const az = p[i * 2 + 1]
      const bx = p[j * 2]
      const bz = p[j * 2 + 1]

      const ex = bx - ax
      const ez = bz - az
      const wx = x - ax
      const wz = z - az
      const t = clampUnit((wx * ex + wz * ez) / (ex * ex + ez * ez))
      const cx = wx - ex * t
      const cz = wz - ez * t
      d = Math.min(d, cx * cx + cz * cz)

      // Winding test: three comparisons decide whether the ray crosses.
      const c1 = z >= az
      const c2 = z < bz
      const c3 = ex * wz > ez * wx
      if ((c1 && c2 && c3) || (!c1 && !c2 && !c3)) sign = -sign
    }
    return sign * Math.sqrt(d)
  }
}

/** Rectangle with the corners cut off — the "Свалка" slab. */
export function chamferedRect(
  cx: number,
  cz: number,
  halfX: number,
  halfZ: number,
  cut: number,
): Polygon {
  return new Polygon([
    [cx - halfX + cut, cz - halfZ],
    [cx + halfX - cut, cz - halfZ],
    [cx + halfX, cz - halfZ + cut],
    [cx + halfX, cz + halfZ - cut],
    [cx + halfX - cut, cz + halfZ],
    [cx - halfX + cut, cz + halfZ],
    [cx - halfX, cz + halfZ - cut],
    [cx - halfX, cz - halfZ + cut],
  ])
}

/**
 * Union of `add` minus union of `sub`. Distances are exact for the primitives
 * and conservative near the seams, which is fine — nothing depends on the
 * gradient being perfect, only on the sign and the rough magnitude.
 */
export class CompositeShape implements Shape {
  constructor(
    readonly add: Shape[],
    readonly sub: Shape[] = [],
  ) {}

  distance(x: number, z: number): number {
    let d = Infinity
    for (const s of this.add) d = Math.min(d, s.distance(x, z))
    for (const s of this.sub) d = Math.max(d, -s.distance(x, z))
    return d
  }
}

/**
 * Uniform scale about the origin. Lets an arena be authored at comfortable
 * round numbers and then resized as a whole, which is how all four maps were
 * shrunk once screenshots showed the fighters were the size of a crate.
 *
 * Valid because every arena in the game is centred on the origin.
 */
export class ScaledShape implements Shape {
  constructor(
    private readonly inner: Shape,
    private readonly factor: number,
  ) {}

  distance(x: number, z: number): number {
    return this.inner.distance(x / this.factor, z / this.factor) * this.factor
  }
}

function clampUnit(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}
